import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DecomModel, DecomSuspendedUserModel } from '../../../../../types';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../shared/analytics/module-list';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
interface SuspendedUsersGridToolbarProps {
  setOpenPurgeUsers: Dispatch<SetStateAction<boolean>>;
  setOpenTerminateUsers: Dispatch<SetStateAction<boolean>>;
  setOpenPauseUsers: Dispatch<SetStateAction<boolean>>;
  setOpenUnpauseUsers: Dispatch<SetStateAction<boolean>>;
  selectedRows: string[];
  decomSuspendedUsers: DecomSuspendedUserModel[];
  decomModels?: DecomModel[];
}

export default function SuspendedUsersGridToolbar({
  setOpenPurgeUsers,
  setOpenTerminateUsers,
  setOpenPauseUsers,
  setOpenUnpauseUsers,
  selectedRows,
  decomSuspendedUsers,
  decomModels,
}: SuspendedUsersGridToolbarProps) {
  const queryClient = useQueryClient();
  const selectedUserModels =
    decomSuspendedUsers.filter((model) =>
      selectedRows.includes(model.userId + model.creationDate)
    ) || [];
  const hasCompletedModels = selectedUserModels.some(
    (model) => model.decomComplete
  );
  const hasTerminatedModels = selectedUserModels.some(
    (model) => model.isTerminated && !model.decomComplete
  );
  const hasPausedModels = selectedUserModels.some(
    (model) => !model.isTerminated && !model.isactive && !model.decomComplete
  );
  const hasUnpausedModels = selectedUserModels.some(
    (model) => !model.isTerminated && model.isactive && !model.decomComplete
  );
  const hasMissingDecomModel = selectedUserModels.some(
    (model) =>
      !model.decomModelKeyString ||
      !decomModels?.find((d) => d.id === model.decomModelKeyString)
  );

  return (
    <SharedToolbar
      secondaryButtons={
        <>
          <SharedIconButton
            variant="pause"
            onClick={() => setOpenPauseUsers(true)}
            disabled={
              selectedRows.length === 0 ||
              hasPausedModels ||
              hasCompletedModels ||
              hasMissingDecomModel
            }
            tooltipLabel={
              hasMissingDecomModel
                ? 'Cannot pause: one or more selected users have no associated decom policy.'
                : hasCompletedModels
                ? 'You cannot pause users that are already completed. Please unselect any completed users.'
                : hasPausedModels
                ? 'You cannot pause users that are already paused. Please unselect any paused users.'
                : 'Pause'
            }
          />
          <SharedIconButton
            variant="run"
            onClick={() => setOpenUnpauseUsers(true)}
            disabled={
              selectedRows.length === 0 ||
              hasUnpausedModels ||
              hasCompletedModels ||
              hasMissingDecomModel
            }
            tooltipLabel={
              hasMissingDecomModel
                ? 'Cannot unpause: one or more selected users have no associated decom policy.'
                : hasCompletedModels
                ? 'You cannot unpause users that are already completed. Please unselect any completed users.'
                : hasUnpausedModels
                ? 'You cannot unpause users that are already unpaused. Please unselect any unpaused users.'
                : 'Unpause'
            }
          />
          <SharedIconButton
            variant="close"
            onClick={() => setOpenTerminateUsers(true)}
            disabled={
              selectedRows.length === 0 ||
              hasTerminatedModels ||
              hasCompletedModels
            }
            tooltipLabel={
              hasCompletedModels
                ? 'You cannot terminate users that are already completed. Please unselect any completed users.'
                : hasTerminatedModels
                ? 'You cannot terminate users that are already terminated. Please unselect any terminated users.'
                : 'Terminate'
            }
          />
          <SharedIconButton
            variant="deleteForever"
            onClick={() => setOpenPurgeUsers(true)}
            disabled={selectedRows.length === 0}
            tooltipLabel="Purge"
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['SuspendedUsers'] });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="decomSuspendedUsersGridStates"
            module={ModuleList.modules.automation.name}
            section={ModuleList.modules.automation.sections.decomSuspended}
          />
        </>
      }
    />
  );
}
